import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOSchema from '@/components/feature/SEOSchema';

const milestones = [
  { year: '2023', title: '项目启航', description: '草莓导航项目正式启动，核心团队组建完成，开始网站资源的收集与整理工作。' },
  { year: '2024', title: '初版上线', description: '第一版网站上线，涵盖5大分类、200+优质网站资源，获得首批用户好评。' },
  { year: '2024', title: '分类扩展', description: '扩展至8大分类，新增设计创意、开发技术、生活服务等板块，资源数量翻倍。' },
  { year: '2025', title: '内容升级', description: '推出推荐文章板块，提供深度的工具评测和使用指南，内容生态更加丰富。' },
  { year: '2025', title: '体验优化', description: '全面升级搜索功能和界面设计，提升用户在移动端和桌面端的浏览体验。' },
  { year: '2026', title: '持续成长', description: '持续优化网站内容质量，致力于成为中文互联网最值得信赖的网站导航平台。' },
];

const stats = [
  { value: '200+', label: '精选网站' },
  { value: '8', label: '核心分类' },
  { value: '50+', label: '子分类' },
  { value: '99.9%', label: '可用率' },
];

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      setFormMessage('感谢您的留言，我们会尽快回复！');
      form.reset();
      setTimeout(() => setFormStatus('idle'), 5000);
      return;
    }

    setFormStatus('submitting');
    setFormMessage('');

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9q7h56e3oq4jqumthig', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言，我们会尽快回复！');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
        if (serverMsg && (serverMsg.includes('spam') || serverMsg.includes('form data is spam'))) {
          setFormStatus('success');
          setFormMessage('感谢您的留言，我们会尽快回复！');
          form.reset();
        } else {
          setFormStatus('error');
          setFormMessage(serverMsg || '提交失败，请稍后重试。');
        }
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请检查网络后重试。');
    }
  };

  return (
    <>
      <title>关于我们 - 草莓导航</title>
      <meta name="description" content="了解草莓导航的品牌故事、发展历程和联系方式。我们致力于为用户提供精选网站导航服务，一站直达优质资源。" />
      <meta name="keywords" content="关于草莓导航,网站导航品牌,联系我们,草莓导航团队" />
      <link rel="canonical" href="https://strawberry-nav.com/about" />

      <SEOSchema
        type="Organization"
        data={{
          name: '草莓导航',
          url: 'https://strawberry-nav.com',
          description: '精选网站导航入口，一站直达优质资源',
          foundingDate: '2023',
          contactPoint: {
            '@type': 'ContactPoint',
            contactType: 'customer service',
            availableLanguage: ['Chinese'],
          },
        }}
      />
      <SEOSchema
        type="WebPage"
        data={{
          name: '关于我们 - 草莓导航',
          url: 'https://strawberry-nav.com/about',
          inLanguage: 'zh-CN',
        }}
      />
      <SEOSchema
        type="BreadcrumbList"
        data={{
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'https://strawberry-nav.com' },
            { '@type': 'ListItem', position: 2, name: '关于我们' },
          ],
        }}
      />
      <SEOSchema
        type="FAQPage"
        data={{
          mainEntity: [
            {
              '@type': 'Question',
              name: '草莓导航是什么？',
              acceptedAnswer: { '@type': 'Answer', text: '草莓导航是一款精心整理的网站导航平台，为用户提供精选网站资源，涵盖工具、娱乐、学习、购物等多个分类，帮助用户快速找到需要的网站。' },
            },
            {
              '@type': 'Question',
              name: '草莓导航的网站资源如何筛选？',
              acceptedAnswer: { '@type': 'Answer', text: '我们的编辑团队会严格筛选每一个收录的网站，确保其质量可靠、内容优质、用户体验好，并定期更新和维护导航资源。' },
            },
            {
              '@type': 'Question',
              name: '如何推荐网站加入草莓导航？',
              acceptedAnswer: { '@type': 'Answer', text: '您可以通过本页面的联系表单提交网站推荐建议，我们的编辑团队会进行审核，符合标准的网站将被收录。' },
            },
          ],
        }}
      />

      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <Breadcrumb items={[{ label: '关于我们' }]} />

        <section className="w-full px-4 md:px-6 py-12 md:py-20 bg-background-50">
          <div className="w-full max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <div className="w-16 h-16 rounded-2xl bg-primary-100 flex items-center justify-center mx-auto mb-5">
                <i className="ri-heart-3-fill text-primary-600 text-3xl"></i>
              </div>
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
                关于草莓导航
              </h1>
              <p className="text-base md:text-lg text-foreground-500 max-w-2xl mx-auto leading-relaxed">
                草莓导航诞生于2023年，由一群热爱互联网的产品人创建。
                我们相信，好的工具应该被更多人发现和使用。
                通过精心筛选和整理，我们希望成为您探索互联网世界的起点。
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-6 mb-16">
              {stats.map((stat) => (
                <div key={stat.label} className="text-center p-6 rounded-2xl bg-background-100 border border-background-200/60">
                  <div className="text-2xl md:text-3xl font-heading font-bold text-primary-600 mb-1">
                    {stat.value}
                  </div>
                  <div className="text-sm text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>

            <div className="mb-16">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 text-center mb-10">
                发展历程
              </h2>
              <div className="relative">
                <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-200 md:-translate-x-px"></div>
                <div className="flex flex-col gap-8">
                  {milestones.map((milestone, index) => (
                    <div
                      key={milestone.year}
                      className={`flex items-start gap-6 ${
                        index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                      }`}
                    >
                      <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right md:pr-10' : 'md:text-left md:pl-10'}`}>
                        <div className="hidden md:block p-5 rounded-2xl bg-background-100 border border-background-200/60">
                          <span className="inline-block px-3 py-1 rounded-lg text-xs font-bold bg-primary-100 text-primary-700 mb-2">
                            {milestone.year}
                          </span>
                          <h3 className="text-base font-heading font-bold text-foreground-900 mb-1">
                            {milestone.title}
                          </h3>
                          <p className="text-sm text-foreground-500 leading-relaxed">
                            {milestone.description}
                          </p>
                        </div>
                      </div>
                      <div className="relative z-10 w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center flex-shrink-0 mt-1">
                        <i className="ri-check-line text-background-50 text-sm"></i>
                      </div>
                      <div className="flex-1 md:hidden">
                        <div className="p-4 rounded-2xl bg-background-100 border border-background-200/60">
                          <span className="inline-block px-2 py-0.5 rounded-md text-xs font-bold bg-primary-100 text-primary-700 mb-1.5">
                            {milestone.year}
                          </span>
                          <h3 className="text-sm font-heading font-bold text-foreground-900 mb-1">
                            {milestone.title}
                          </h3>
                          <p className="text-xs text-foreground-500 leading-relaxed">
                            {milestone.description}
                          </p>
                        </div>
                      </div>
                      {index % 2 !== 0 ? (
                        <div className="hidden md:block flex-1"></div>
                      ) : null}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div id="contact">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 text-center mb-4">
                联系我们
              </h2>
              <p className="text-sm text-foreground-500 text-center mb-10">
                如果您有任何建议、合作意向或网站推荐，欢迎通过以下表单与我们联系
              </p>

              <div className="max-w-2xl mx-auto">
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  className="p-6 md:p-8 rounded-2xl bg-background-100 border border-background-200/60"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        姓名 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 rounded-xl bg-background-50 border border-background-200/60 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-300 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        邮箱 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 rounded-xl bg-background-50 border border-background-200/60 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-300 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                  </div>

                  <div className="mb-5">
                    <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      主题
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      className="w-full px-4 py-2.5 rounded-xl bg-background-50 border border-background-200/60 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-300 transition-colors"
                      placeholder="请选择或输入主题"
                    />
                  </div>

                  <div className="mb-5">
                    <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      留言 <span className="text-primary-500">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      maxLength={500}
                      rows={5}
                      className="w-full px-4 py-2.5 rounded-xl bg-background-50 border border-background-200/60 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-300 transition-colors resize-none"
                      placeholder="请输入您的留言内容（最多500字）"
                    />
                    <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                  </div>

                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="honeypot-field"
                  />

                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="w-full py-3 rounded-xl bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {formStatus === 'submitting' ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin"></i>
                        提交中...
                      </span>
                    ) : '提交留言'}
                  </button>

                  {formStatus === 'success' && (
                    <div className="mt-4 p-4 rounded-xl bg-accent-100 text-accent-800 text-sm text-center animate-fade-in">
                      <i className="ri-checkbox-circle-line mr-1.5"></i>
                      {formMessage}
                    </div>
                  )}
                  {formStatus === 'error' && (
                    <div className="mt-4 p-4 rounded-xl bg-primary-100 text-primary-800 text-sm text-center animate-fade-in">
                      <i className="ri-error-warning-line mr-1.5"></i>
                      {formMessage}
                    </div>
                  )}
                </form>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-10">
                  <div className="flex items-center gap-3 p-4 rounded-xl bg-background-100 border border-background-200/60">
                    <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-accent-600 text-lg"></i>
                    </div>
                    <div>
                      <div className="text-xs text-foreground-500">邮箱</div>
                      <div className="text-sm font-medium text-foreground-900">hello@strawberry-nav.com</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-4 rounded-xl bg-background-100 border border-background-200/60">
                    <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-wechat-line text-accent-600 text-lg"></i>
                    </div>
                    <div>
                      <div className="text-xs text-foreground-500">微信</div>
                      <div className="text-sm font-medium text-foreground-900">StrawberryNav</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-4 rounded-xl bg-background-100 border border-background-200/60">
                    <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-time-line text-accent-600 text-lg"></i>
                    </div>
                    <div>
                      <div className="text-xs text-foreground-500">工作时间</div>
                      <div className="text-sm font-medium text-foreground-900">周一至周五 9:00-18:00</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}